import React, { useEffect, useState } from "react";

import IncomingTransferAcceptSteps from "./IncomingTransferAcceptSteps";
import LockInCountdown from "../LockInCountdown";
import { bdtFmt, DetailRow } from "./InvestorTxnDetailUi";
import {
  acceptIncomingTransfer,
  fetchReceiverTransferPreview,
  rejectIncomingTransfer,
} from "../../services/investmentService";
import { transferStatusLabel, transferStatusTone } from "../../utils/transferLabels";
import { projectTitleWithCode } from "../../utils/projectViews";

export default function IncomingTransferAcceptModal({ tx, project, onClose, onAccepted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [status, setStatus] = useState(tx?.status);

  const busy = accepting || rejecting;
  const projLabel = project ? projectTitleWithCode(project) : String(tx?.project || "Project");
  const tr = tx?.transfer_record;
  const sharesOffered = tr?.shares_offered ?? tx?.shares;
  const lockedNav = Number(tr?.locked_nav_per_share ?? tx?.price_per_share ?? 0);
  const paymentDue = Number(preview?.receiverPaymentBdt || 0);
  const walletBalance = Number(preview?.receiverWalletBdt || 0);

  useEffect(() => {
    setStatus(tx?.status);
  }, [tx?.status]);

  useEffect(() => {
    if (!tx?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setPreview(null);
    setRejectOpen(false);
    setRejectReason("");
    setRejected(false);
    fetchReceiverTransferPreview(tx.id)
      .then(({ data }) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Could not load preview.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tx?.id]);

  const parseApiError = (err, fallback) => {
    const data = err?.response?.data;
    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.rejection_reason === "string") return data.rejection_reason;
    if (Array.isArray(data?.rejection_reason)) return data.rejection_reason[0];
    return fallback;
  };

  const confirmAccept = async () => {
    if (!tx?.id) return;
    setAccepting(true);
    setError("");
    setRejectOpen(false);
    try {
      await acceptIncomingTransfer(tx.id, {});
      onAccepted?.();
      onClose?.();
    } catch (err) {
      setError(parseApiError(err, "Could not complete transfer."));
    } finally {
      setAccepting(false);
    }
  };

  const confirmReject = async () => {
    if (!tx?.id) return;
    const reason = rejectReason.trim();
    if (!reason) {
      setError("Please enter a reason for rejecting this offer.");
      return;
    }
    setRejecting(true);
    setError("");
    try {
      const { data } = await rejectIncomingTransfer(tx.id, { rejection_reason: reason });
      setStatus(data?.status || "REJECTED");
      setRejected(true);
      setRejectOpen(false);
      onAccepted?.();
    } catch (err) {
      setError(parseApiError(err, "Could not reject this offer."));
    } finally {
      setRejecting(false);
    }
  };

  if (!tx) return null;

  const walletAfter =
    preview != null
      ? Math.max(0, Number(preview.receiverWalletBdt || 0) - Number(preview.receiverPaymentBdt || 0))
      : null;
  const netShares = preview?.executableNetShares ?? sharesOffered;
  const showOfferedLine = preview && String(sharesOffered) !== String(netShares);

  return (
    <div className="invx-about-modal-overlay" onClick={busy ? undefined : onClose} role="presentation">
      <div
        className="invx-about-modal investor-offer-detail"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="incoming-accept-title"
        aria-modal="true"
      >
        <button type="button" className="invx-about-modal-close" onClick={onClose} aria-label="Close" disabled={busy}>
          ×
        </button>

        <header className="investor-offer-detail__header">
          <div className="investor-offer-detail__header-main">
            <p className="investor-offer-detail__eyebrow">{rejected ? "Transfer declined" : "Accept transfer"}</p>
            <h3 id="incoming-accept-title" className="investor-offer-detail__title">
              {tx.transfer_ref || projLabel}
            </h3>
            <p className="investor-offer-detail__project">{projLabel}</p>
            <p className="investor-offer-detail__recipient-line">
              From <strong>{preview?.senderDisplayName || "—"}</strong>
            </p>
          </div>
          <span className={`badge ${transferStatusTone(status)}`}>{transferStatusLabel(status)}</span>
        </header>

        <div className="investor-offer-detail__progress">
          <IncomingTransferAcceptSteps
            requestedAt={tx.requested_at}
            accepting={accepting}
            rejecting={rejecting}
            rejected={rejected}
          />
        </div>

        {rejected ? (
          <div className="investor-offer-detail__reject-success" role="status">
            <p className="msg msg-success" style={{ margin: 0 }}>
              This transfer offer has been <strong>rejected</strong>. No amount was deducted from your wallet — your
              balance remains <strong>{bdtFmt.format(walletBalance)}</strong>.
            </p>
            {rejectReason.trim() ? (
              <p className="investor-offer-detail__hint muted" style={{ marginTop: "0.5rem" }}>
                <strong>Reason shared with sender:</strong> {rejectReason.trim()}
              </p>
            ) : null}
          </div>
        ) : loading ? (
          <p className="muted">Loading settlement preview…</p>
        ) : preview ? (
          <>
            <div className="investor-offer-detail__panels">
              <section className="investor-offer-detail__panel" aria-labelledby="incoming-accept-offer">
                <h4 id="incoming-accept-offer" className="investor-offer-detail__panel-title">
                  Offer
                </h4>
                {showOfferedLine ? <DetailRow label="Shares offered">{sharesOffered}</DetailRow> : null}
                <DetailRow label="Shares you receive">{netShares}</DetailRow>
                <DetailRow label="Locked NAV / share">{bdtFmt.format(lockedNav)}</DetailRow>
              </section>

              <section className="investor-offer-detail__panel" aria-labelledby="incoming-accept-wallet">
                <h4 id="incoming-accept-wallet" className="investor-offer-detail__panel-title">
                  Your wallet
                </h4>
                <DetailRow label="Balance now">{bdtFmt.format(walletBalance)}</DetailRow>
                <DetailRow label="Payment due">{bdtFmt.format(paymentDue)}</DetailRow>
                <DetailRow label="Balance after">{walletAfter != null ? bdtFmt.format(walletAfter) : "—"}</DetailRow>
              </section>
            </div>

            {preview.redemptionUnlockAt ? (
              <p className="investor-offer-detail__hint muted">
                Redemption opens after lock-in:{" "}
                {new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
                  new Date(preview.redemptionUnlockAt)
                )}
                <LockInCountdown unlockIso={preview.redemptionUnlockAt} />
              </p>
            ) : null}
          </>
        ) : null}

        {!rejected && rejectOpen ? (
          <div className="investor-offer-detail__reject">
            <label htmlFor="incoming-reject-reason" className="investor-offer-detail__reject-label">
              Reason for rejecting
            </label>
            <textarea
              id="incoming-reject-reason"
              className="investor-offer-detail__reject-input"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Price not acceptable, holding limit reached, timing…"
              disabled={busy}
              maxLength={4000}
            />
          </div>
        ) : null}

        {error ? <p className="msg msg-error">{error}</p> : null}

        <footer className="investor-offer-detail__footer">
          {rejected ? (
            <button type="button" className="invx-trade-submit compact-btn" onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button type="button" className="btn-secondary compact-btn" onClick={onClose} disabled={busy}>
                Close
              </button>
              {rejectOpen ? (
                <>
                  <button
                    type="button"
                    className="btn-secondary compact-btn"
                    disabled={busy}
                    onClick={() => {
                      setRejectOpen(false);
                      setRejectReason("");
                      setError("");
                    }}
                  >
                    Back
                  </button>
                  <button type="button" className="btn-danger compact-btn" disabled={busy} onClick={confirmReject}>
                    {rejecting ? "Rejecting…" : "Confirm rejection"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-danger compact-btn"
                    disabled={busy || loading || !preview}
                    onClick={() => {
                      setRejectOpen(true);
                      setError("");
                    }}
                  >
                    Reject offer
                  </button>
                  <button
                    type="button"
                    className="invx-trade-submit compact-btn"
                    disabled={busy || loading || !preview}
                    onClick={confirmAccept}
                  >
                    {accepting ? "Processing…" : "Confirm and accept"}
                  </button>
                </>
              )}
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
